// Branded public-route fallback. This stays in the eager app shell so an
// unknown direct URL always has a recoverable marketing surface, even when
// the visitor never first loaded the landing route.

import { Home, SearchX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Eyebrow } from "@/components/marketing-primitives";

type MarketingStatusPageProps = {
  readonly icon: React.ReactNode;
  readonly eyebrow: string;
  readonly title: string;
  readonly body: string;
  readonly action: string;
};

/** Shared visual recovery surface for public-route misses and route errors. */
export function MarketingStatusPage({
  icon,
  eyebrow,
  title,
  body,
  action,
}: MarketingStatusPageProps): React.ReactElement {
  return (
    <section className="bg-[var(--bg-surface)] px-6 py-[88px] lg:px-8 lg:py-[120px]">
      <div className="mx-auto flex max-w-[640px] flex-col items-center text-center">
        <span className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-[16px] bg-[var(--mkt-tint-accent)] text-[var(--accent-hover)]">
          {icon}
        </span>
        <Eyebrow className="mb-3">{eyebrow}</Eyebrow>
        <h1 className="m-0 font-display text-[clamp(2rem,5vw,3.5rem)] font-semibold leading-[1.08] tracking-[-0.025em] text-[var(--fg-primary)]">
          {title}
        </h1>
        <p className="mb-0 mt-4 max-w-[480px] font-serif text-[17px] leading-relaxed text-[var(--fg-secondary)]">
          {body}
        </p>
        <Link
          to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-[9px] bg-[var(--accent)] px-4 py-2.5 font-sans text-sm font-semibold text-[var(--fg-on-accent)] no-underline transition-colors hover:bg-[var(--accent-hover)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-surface)]"
        >
          <Home size={16} aria-hidden="true" />
          {action}
        </Link>
      </div>
    </section>
  );
}

export function NotFoundPage(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <MarketingStatusPage
      icon={<SearchX size={28} aria-hidden="true" />}
      eyebrow={t("not_found.eyebrow")}
      title={t("not_found.title")}
      body={t("not_found.body")}
      action={t("not_found.action")}
    />
  );
}
